import { useState } from "react";
import { Gift, Loader2, Mail, Star } from "lucide-react";
import { toast } from "sonner";

const euros = (c) => (Number(c || 0) / 100).toFixed(2);
const inp = "w-full rounded-xl border border-amber-800/20 bg-white px-4 py-3 text-sm outline-none focus:border-amber-deep";

export default function RelativeCheckout({ api, token, cart, subtotal, childName, pointsPerEuro = 1, onDone, errMsg }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "" });
  const [pay, setPay] = useState("card");
  const [account, setAccount] = useState(true);
  const [newsletter, setNewsletter] = useState(true);
  const [busy, setBusy] = useState(false);
  const up = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const points = Math.floor((subtotal / 100) * pointsPerEuro);

  const submit = async () => {
    if (!cart.length) return toast.error("Ajoutez au moins un produit");
    if (f.name.trim().length < 2 || !f.email.includes("@") || f.phone.trim().length < 6) return toast.error("Indiquez votre nom, e-mail et téléphone");
    if (account && f.password.length < 8) return toast.error("Choisissez un mot de passe d'au moins 8 caractères (ou décochez la création de compte)");
    setBusy(true);
    try {
      const { data } = await api.post(`/partage/${token}/commande`, {
        items: cart.map((p) => ({ product_id: p.product_id, qty: p.q })), name: f.name, email: f.email, phone: f.phone,
        payment_method: pay, create_account: account, password: account ? f.password : "", newsletter, origin_url: window.location.origin,
      });
      if (data.account_created) toast.success("Votre compte Pétales de Miel est créé !");
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      onDone({ ...data, status: "pending_payment", payment_method: pay });
    } catch (e) { toast.error(errMsg(e, "Commande impossible, réessayez")); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="relative-checkout" className="h-fit space-y-4 rounded-3xl border border-amber-800/15 bg-white p-6 shadow-sm lg:sticky lg:top-6">
      <p className="font-serif text-2xl font-semibold text-bark">Ma commande</p>
      {cart.length === 0 ? <p className="text-sm text-bark-soft">Choisissez vos produits avec les boutons +.</p> : (
        <ul className="space-y-1 text-sm">
          {cart.map((p) => <li key={p.product_id} className="flex justify-between gap-2"><span className="text-bark">{p.q} × {p.name}{p.disc ? <span className="ml-1 text-xs text-sage">−{p.disc} %</span> : null}</span><span className="font-medium">{euros(p.unit * p.q)} €</span></li>)}
        </ul>
      )}
      <p data-testid="relative-total" className="flex justify-between border-t border-amber-900/10 pt-3 font-serif text-xl font-semibold text-bark"><span>Total</span><span>{euros(subtotal)} €</span></p>

      <div className="space-y-2">
        <input data-testid="relative-name" value={f.name} onChange={up("name")} placeholder="Nom et prénom *" className={inp} />
        <input data-testid="relative-email" type="email" value={f.email} onChange={up("email")} placeholder="E-mail *" className={inp} />
        <input data-testid="relative-phone" value={f.phone} onChange={up("phone")} placeholder="Téléphone *" className={inp} />
      </div>

      <div className="grid grid-cols-2 gap-2">
        {[["card", "PayPal ou carte bancaire"], ["cheque", `Chèque remis à ${childName || "l'élève"}`]].map(([k, l]) => (
          <button key={k} type="button" data-testid={`relative-pay-${k}`} onClick={() => setPay(k)} className={`rounded-xl border px-3 py-2.5 text-xs font-semibold ${pay === k ? "border-amber-deep bg-amber-50 text-bark" : "border-amber-900/15 text-bark-soft"}`}>{l}</button>
        ))}
      </div>

      <div data-testid="relative-account-offer" className={`rounded-2xl border-2 p-4 transition-colors ${account ? "border-amber-deep bg-amber-50/70" : "border-amber-900/10"}`}>
        <label className="flex cursor-pointer items-start gap-3">
          <input type="checkbox" data-testid="relative-create-account" checked={account} onChange={(e) => setAccount(e.target.checked)} className="mt-1 h-4 w-4 accent-amber-deep" />
          <span>
            <span className="block font-semibold text-bark">Créer mon compte gratuit — recommandé</span>
            <span className="mt-1 flex items-center gap-1.5 text-xs text-bark"><Star size={13} className="fill-honey text-honey" /> {points > 0 ? <>Gagnez <strong>{points} point{points > 1 ? "s" : ""} de fidélité</strong> avec cette commande, à utiliser sur la boutique</> : "Cumulez des points de fidélité à chaque commande"}</span>
            <span className="mt-1 flex items-center gap-1.5 text-xs text-bark"><Gift size={13} className="text-amber-deep" /> Suivi de vos commandes et réductions réservées aux membres</span>
          </span>
        </label>
        {account && <input data-testid="relative-password" type="password" value={f.password} onChange={up("password")} placeholder="Mot de passe (8 caractères min.)" className={`${inp} mt-3`} />}
      </div>

      <label className="flex cursor-pointer items-start gap-3 text-xs text-bark">
        <input type="checkbox" data-testid="relative-newsletter" checked={newsletter} onChange={(e) => setNewsletter(e.target.checked)} className="mt-0.5 h-4 w-4 accent-amber-deep" />
        <span className="flex items-start gap-1.5"><Mail size={13} className="mt-0.5 shrink-0 text-amber-deep" /> Recevoir la lettre de Pétales de Miel : conseils de l'apicultrice, recettes au miel et offres de saison (désinscription en un clic)</span>
      </label>

      <button data-testid="relative-submit" onClick={submit} disabled={busy || !cart.length} className="flex w-full items-center justify-center gap-2 rounded-full bg-amber-deep py-3.5 text-sm font-semibold text-white hover:bg-honey disabled:opacity-60">
        {busy && <Loader2 size={16} className="animate-spin" />} {pay === "card" ? `Payer ${euros(subtotal)} € avec PayPal ou carte` : "Valider ma commande"}
      </button>
    </div>
  );
}
